/**
 * BT 수료증 · 완성보고서 · 교육이력 · 출석 코드 API (bt-service /api/bt)
 */
const BASE = '/api/bt';

export class ApiError extends Error {
  status: number;
  constructor(status: number, message: string) {
    super(message);
    this.status = status;
  }
}

export async function api<T = any>(path: string, options: { method?: string; body?: any; form?: FormData } = {}): Promise<T> {
  const init: RequestInit = { method: options.method || (options.body || options.form ? 'POST' : 'GET'), credentials: 'include' };
  if (options.form) init.body = options.form;
  else if (options.body !== undefined) {
    init.body = JSON.stringify(options.body);
    init.headers = { 'Content-Type': 'application/json' };
  }
  let response: Response;
  try {
    response = await fetch(`${BASE}${path}`, init);
  } catch {
    throw new ApiError(0, '서버에 연결할 수 없습니다. 인터넷 연결을 확인해주세요.');
  }
  const type = response.headers.get('content-type') || '';
  const data = type.includes('application/json') ? await response.json() : { message: await response.text() };
  if (!response.ok || data?.success === false) {
    const message = data?.message || data?.error;
    if (response.status === 413) throw new ApiError(413, '파일이 너무 큽니다. 사진 크기를 줄여서 다시 올려주세요.');
    throw new ApiError(response.status, message && String(message).length < 300 ? message : `요청을 처리하지 못했습니다. (${response.status})`);
  }
  return data as T;
}

export const fileUrl = (path: string) => `${BASE}${path}`;

// ───────── 타입 ─────────
export type ClubCode = 'PUGGLES' | 'CUBBIES' | 'SPARKS' | 'T&T' | 'TREK' | 'JOURNEY' | 'PC' | 'CM';
export type ReportStatus = 'submitted' | 'received' | 'checking' | 'approved' | 'rejected';

export interface CertData {
  _id: string;
  serial: string;
  club: ClubCode;
  clubLabel: string;
  bg: '01' | '02' | '03' | '04';
  teacherName: string;
  teacherNameEn: string;
  churchName: string;
  regNo?: string;
  completedAt: string;
  issuedAt?: string;
  status?: 'issued' | 'revoked';
  signatureUrl: string;
  dateChangedAt?: string | null;
  needsReReceive?: boolean;
  churchReceipt?: { pdf: number; print: number; lastAt: string | null; lastWho: string };
}

export interface Checklist {
  step1Attended?: boolean;
  reportVerified?: boolean;
  step1Auto?: boolean;
  step2Score?: number | null;
  step3Verses?: boolean[];
  step4Visit?: boolean;
  step4VisitChurch?: string;
  step4Practice?: boolean;
  step4Signed?: boolean;
  signTeacher?: boolean;
  signCoordinator?: boolean;
  memo?: string;
}

export interface ReportListItem {
  _id: string;
  batchId: string;
  source: 'church' | 'email';
  regNo: string;
  churchName: string;
  region?: string;
  managerName: string;
  managerPhone: string;
  teacherId?: string | null;
  teacherName: string;
  club: ClubCode;
  status: ReportStatus;
  submittedAt: string;
  receivedAt?: string;
  checkingAt?: string;
  decidedAt?: string;
  requestedDate?: string;
  requestedDateReason?: string;
  fileCount: number;
  ready: boolean;
  problems: string[];
  serial?: string;
  rejectReason?: string;
}

export interface Notifications {
  pendingSheets: number;
  unread: Array<{ batchId: string; churchName: string; regNo: string; managerName: string; submittedAt: string; count: number; dateRequests: number; resubmit: boolean }>;
  email: { enabled: boolean; to: string; configured: boolean };
}
